import { TriangleAlert } from "lucide-react";
import React, { memo, useMemo, useState } from "react";

import { getStationName } from "../model/utils";

import { StyledTableCell, StyledTableHead } from "./styled-table-components";

import type { OfferOrderDTO, OrderDetailResponseDTO } from "@/common/api";
import {
  OrderDetailResponseDTOStatus,
  useContinueRebidding,
  useGetAllOffers,
  useGetPivotTable,
} from "@/common/api";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TruncateTooltip,
} from "@/common/components";
import { useAuth, useToast } from "@/common/hooks";
import { TARIFF_LABELS } from "@/common/model";

export interface ParticipantBid {
  initial?: string;
  rebiddings: Record<number, string>;
}

export interface Participant {
  id: string;
  name: string;
  bids: Record<string, ParticipantBid>;
  countWagonsOwner?: number;
}

export interface RebiddingRowData {
  id: string;
  roadStation: string;
  yourProposal?: string;
  unit: string;
}

interface OfferFormProps {
  order: OrderDetailResponseDTO;
  detail?: OfferOrderDTO[];
  isReadOnly?: boolean;
}

export const OwnerRebiddingForm = memo(({ order, isReadOnly }: OfferFormProps) => {
  const { auth } = useAuth();
  const { toast } = useToast();

  const { data: offersData, refetch: refetchOffersData } = useGetAllOffers(
    { orderId: order.id! },
    {
      query: {
        enabled: auth.isCargoOwner,
      },
    },
  );

  const hasAccess = !auth.isAdmin && Boolean(order?.isOwner);
  const allowRetradingRound = offersData?.data?.payload?.allowRetradingRound;
  const [isRebidding, isSummingUp, isCompleted] = [
    order.status === OrderDetailResponseDTOStatus.REBIDDING,
    order.status === OrderDetailResponseDTOStatus.SUMMING_UP,
    order.status === OrderDetailResponseDTOStatus.COMPLETED,
  ];

  const { data: pivotTableData, isLoading: isPivotTableLoading } = useGetPivotTable(
    {
      orderId: order.id!,
    },
    {
      query: {
        enabled: isRebidding || isSummingUp || isCompleted,
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const pivotPayload = pivotTableData?.data?.payload as any;
  const directions = useMemo(
    () => (pivotPayload?.directions ?? []) as any[],
    [pivotPayload?.directions],
  );
  const ownerStages = useMemo(
    () => (pivotPayload?.ownerStages ?? []) as any[],
    [pivotPayload?.ownerStages],
  );
  const sobStages = useMemo(
    () => (pivotPayload?.sobStages ?? []) as any[],
    [pivotPayload?.sobStages],
  );

  // Преобразуем направления (дорога/станция) из API в формат для формы
  const tableRebiddingData = useMemo<RebiddingRowData[]>(() => {
    return directions
      .map((direction: any, index: number) => {
        // Ключ строки – индекс направления из бэка.
        // Он совпадает с indexNumber в payload.ownerStages[*].offers[*].indexNumber.
        const id = String(
          direction.index ??
            // запасной вариант – позиция в массиве
            index,
        );

        const roadStation =
          // если с бэка уже пришла склеенная строка
          (direction as any).roadStation ??
          // иначе собираем из дороги и станции как в других формах
          getStationName(direction.unloadingRoad as never, direction.unloadingStation as never) ??
          "-";

        const unit =
          direction.unit ??
          (direction.tariffRoad && TARIFF_LABELS[direction.tariffRoad as keyof typeof TARIFF_LABELS]
            ? TARIFF_LABELS[direction.tariffRoad as keyof typeof TARIFF_LABELS]
            : "-");

        const yourProposal =
          direction.yourProposal !== null && direction.yourProposal !== undefined
            ? String(direction.yourProposal)
            : undefined;

        return {
          id,
          roadStation,
          yourProposal,
          unit,
        };
      })
      .filter((row) => row.id);
  }, [directions]);

  // Формируем участников и их ставки (переторжки) из API
  // Берём данные из sobStages: массив участников, у каждого массив stages с offers
  const tableParticipants = useMemo<Participant[]>(() => {
    if (!Array.isArray(sobStages)) return [];

    return sobStages.map((participant: any, index: number) => {
      const bids: Record<string, ParticipantBid> = {};

      const stages = Array.isArray(participant.stages) ? participant.stages : [];

      let countWagonsOwner: number | undefined;

      stages.forEach((stage: any) => {
        const stageNumber = typeof stage.stage === "number" ? stage.stage : 0;
        const offers = Array.isArray(stage.offers) ? stage.offers : [];

        if (countWagonsOwner === undefined && typeof stage.countWagonsOwner === "number") {
          countWagonsOwner = stage.countWagonsOwner;
        }

        offers.forEach((offer: any) => {
          const directionKey =
            offer.indexNumber !== undefined && offer.indexNumber !== null
              ? String(offer.indexNumber)
              : "";

          if (!directionKey) return;

          if (!bids[directionKey]) {
            bids[directionKey] = { initial: undefined, rebiddings: {} };
          }

          const bidForDirection = bids[directionKey];

          const value =
            offer.wagonTariff !== null && offer.wagonTariff !== undefined
              ? String(offer.wagonTariff)
              : undefined;

          if (!value) return;

          if (stageNumber === 0) {
            // Стартовое предложение участника
            bidForDirection.initial = value;
          } else if (stageNumber > 0) {
            // Переторжки 1..N (номер берём из stage)
            bidForDirection.rebiddings[stageNumber] = value;
          }
        });
      });

      return {
        id: String(participant.participantNumber ?? index + 1),
        name:
          auth.isAdmin && participant.companyName?.trim()
            ? participant.companyName
            : (participant.name ??
              (participant.participantNumber
                ? `Участник ${participant.participantNumber}`
                : `Участник ${index + 1}`)),
        bids,
        countWagonsOwner,
      };
    });
  }, [sobStages, auth.isAdmin]);

  const filteredParticipants = tableParticipants;

  // Определяем максимальное количество переторжек по данным с бэка (stage > 0)
  const maxRebiddingCount = useMemo(() => {
    // Максимальный номер этапа среди sobStages (по участникам)
    const sobStagesMax =
      sobStages?.reduce((max: number, participant: any) => {
        const stages = Array.isArray(participant.stages) ? participant.stages : [];

        stages.forEach((s: any) => {
          const stageNum = typeof s.stage === "number" ? s.stage : 0;
          if (stageNum > 0) {
            max = Math.max(max, stageNum);
          }
        });

        return max;
      }, 0) ?? 0;

    // Максимальный номер этапа среди ownerStages (грузовладелец)
    const ownerStagesMax =
      ownerStages?.reduce((max: number, s: any) => {
        const stageNum = typeof s.stage === "number" ? s.stage : 0;
        if (stageNum > 0) {
          return Math.max(max, stageNum);
        }
        return max;
      }, 0) ?? 0;

    return Math.max(sobStagesMax, ownerStagesMax);
  }, [sobStages, ownerStages]);

  // Функция для получения значения переторжки по номеру
  const getRebiddingValue = (
    bid: ParticipantBid | undefined,
    index: number,
  ): string | undefined => {
    if (!bid) return undefined;
    return bid.rebiddings[index];
  };

  // Значения в колонке "Ваше предложение"
  const proposals = useMemo<Record<string, string>>(() => {
    const acc: Record<string, string> = {};

    // Базовые значения из directions.yourProposal
    tableRebiddingData.forEach((row: RebiddingRowData) => {
      if (row.yourProposal) {
        acc[row.id] = row.yourProposal;
      }
    });

    // Данные из ownerStages (последний этап переторжки грузовладельца)
    if (ownerStages.length > 0) {
      // Находим последний stage с ненулевыми offers
      const stagesWithOffers = ownerStages.filter(
        (s: any) => Array.isArray(s.offers) && s.offers.length > 0,
      ) as any[];

      const stageToUse =
        stagesWithOffers.length > 0
          ? stagesWithOffers[stagesWithOffers.length - 1]
          : (ownerStages[ownerStages.length - 1] as any);

      const offers = Array.isArray(stageToUse.offers) ? (stageToUse.offers as any[]) : [];

      offers.forEach((offer: any) => {
        if (
          offer.indexNumber === undefined ||
          offer.indexNumber === null ||
          offer.wagonTariff === undefined ||
          offer.wagonTariff === null
        ) {
          return;
        }

        const id = String(offer.indexNumber);
        acc[id] = String(offer.wagonTariff);
      });
    }

    return acc;
  }, [tableRebiddingData, ownerStages]);

  const totalWagonsRequired = useMemo(() => {
    // Приоритет: totalWagons из первого ownerStage, у которого есть offers
    for (const stage of ownerStages) {
      const offers = Array.isArray(stage.offers) ? stage.offers : [];

      if (offers.length > 0 && typeof stage.totalWagons === "number") {
        return stage.totalWagons;
      }
    }

    // Фолбэк: общее количество вагонов из totalWagons
    if (typeof pivotPayload?.totalWagons === "number") {
      return pivotPayload.totalWagons;
    }

    return "-";
  }, [ownerStages, pivotPayload?.totalWagons]);

  const { mutateAsync: continueRebiddingAsync, isPending } = useContinueRebidding();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDayCount, setSelectedDayCount] = useState<string>("1");

  const handleContinueRebiddingClick = () => {
    setIsModalOpen(true);
  };

  const handleModalConfirm = () => {
    const dayCount = parseInt(selectedDayCount, 10);

    continueRebiddingAsync({
      data: {
        orderId: order.id!,
        userId: auth.id!,
        dayCount,
      },
    })
      .then(() => {
        return refetchOffersData();
      })
      .then(() => {
        setIsModalOpen(false);
        toast({
          title: "Успешно",
          description: "Запрос на продление переторжки отправлен",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description:
            error instanceof Error
              ? error.message
              : "Не удалось отправить запрос на продление переторжки",
        });
      });
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  if (isPivotTableLoading || !pivotTableData?.data?.payload) {
    return (
      <div className="mb-6">
        <p className="text-Text-secondary">Загрузка данных...</p>
      </div>
    );
  }

  return (
    <>
      <form noValidate>
        <div className="mb-5 md:mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-Text-main leading-7 p-3 md:p-4">
            Сводная таблица по переторжке
          </h2>
          <Table className="min-w-[600px] md:min-w-full mb-3 md:mb-0">
            <TableHeader>
              <TableRow>
                <StyledTableHead rowSpan={filteredParticipants.length > 0 ? 2 : 1}>
                  Дорога/станция выгрузки
                </StyledTableHead>
                <StyledTableHead rowSpan={filteredParticipants.length > 0 ? 2 : 1}>
                  {auth.isAdmin ? "Предложение грузовлалельца" : "Ваше предложение"}
                </StyledTableHead>
                {filteredParticipants.length > 0 &&
                  filteredParticipants.map((participant, index) => (
                    <StyledTableHead
                      key={participant.id}
                      colSpan={1 + maxRebiddingCount}
                      className={`text-center ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                    >
                      {participant.name}
                    </StyledTableHead>
                  ))}
                <StyledTableHead rowSpan={filteredParticipants.length > 0 ? 2 : 1}>
                  Тип тарифа
                </StyledTableHead>
              </TableRow>
              {filteredParticipants.length > 0 && (
                <TableRow>
                  {filteredParticipants.map((participant, index) => (
                    <React.Fragment key={participant.id}>
                      <StyledTableHead className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}>
                        начальное
                      </StyledTableHead>
                      {Array.from({ length: maxRebiddingCount }, (_, i) => i + 1).map((num) => (
                        <StyledTableHead
                          key={num}
                          className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                        >
                          переторжка {num}
                        </StyledTableHead>
                      ))}
                    </React.Fragment>
                  ))}
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {tableRebiddingData.map((row) => (
                <TableRow key={row.id}>
                  <StyledTableCell>
                    <TruncateTooltip text={row.roadStation || "-"} />
                  </StyledTableCell>
                  {/* Значение для "Ваше предложение" — одна колонка */}
                  <StyledTableCell>
                    {proposals[row.id] || row.yourProposal || (
                      <span className="text-gray-400">-</span>
                    )}
                  </StyledTableCell>

                  {filteredParticipants.map((participant, index) => {
                    const bid = participant.bids[row.id];
                    const bgClass = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                    return (
                      <React.Fragment key={participant.id}>
                        <StyledTableCell className={bgClass}>
                          {bid?.initial || <span className="text-gray-400">-</span>}
                        </StyledTableCell>
                        {Array.from({ length: maxRebiddingCount }, (_, i) => i + 1).map((num) => {
                          const rebiddingValue = getRebiddingValue(bid, num);
                          return (
                            <StyledTableCell key={num} className={bgClass}>
                              {rebiddingValue || <span className="text-gray-400">-</span>}
                            </StyledTableCell>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <StyledTableCell>{row.unit}</StyledTableCell>
                </TableRow>
              ))}
              <TableRow>
                <StyledTableCell>
                  <b>Итого вагонов</b>
                </StyledTableCell>
                <StyledTableCell>
                  <b>{totalWagonsRequired}</b>
                </StyledTableCell>
                {filteredParticipants.map((participant, index) => {
                  const bgClass = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                  const totalWagons = participant.countWagonsOwner ?? "";
                  return (
                    <React.Fragment key={participant.id}>
                      <StyledTableCell className={bgClass}>
                        <b>{totalWagons}</b>
                      </StyledTableCell>
                      {Array.from({ length: maxRebiddingCount }, (_, i) => i + 1).map((num) => (
                        <StyledTableCell key={num} className={bgClass}>
                          {/* Для строк "Итого" в переторжках по участнику оставляем пусто */}
                        </StyledTableCell>
                      ))}
                    </React.Fragment>
                  );
                })}
                <StyledTableCell>
                  <b>шт.</b>
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <div
          className={`flex gap-4 flex-col md:flex-row ${
            order.moderationRetradingComment ? "justify-between" : "justify-end"
          }`}
        >
          {order.moderationRetradingComment && !isCompleted && (
            <Alert variant="destructive" className="basis-1/2 grow-0 bg-red-100 border-red-800">
              <TriangleAlert className="size-4" />
              <AlertTitle className="text-red-800 text-sm">Комментарий модератора:</AlertTitle>
              <AlertDescription className="text-xs">
                {order.moderationRetradingComment}
              </AlertDescription>
            </Alert>
          )}
          {hasAccess && (
            <Button
              variant="outline"
              type="button"
              disabled={
                isPending ||
                allowRetradingRound ||
                isSummingUp ||
                !!order.moderationRetradingComment ||
                isCompleted
              }
              onClick={handleContinueRebiddingClick}
              className="px-6 md:px-10 py-2 md:py-3 text-sm md:text-base h-auto md:h-10"
            >
              {isPending ? "Отправка..." : "Запросить продление переторжки"}
            </Button>
          )}
        </div>
      </form>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Продление переторжки</DialogTitle>
            <DialogDescription>Выберите количество дней</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedDayCount} onValueChange={setSelectedDayCount}>
              <SelectContent>
                {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={String(day)}>
                    {day} {day === 1 ? "день" : day < 5 ? "дня" : "дней"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleModalCancel} disabled={isPending}>
              Отмена
            </Button>
            <Button onClick={handleModalConfirm} disabled={isPending}>
              {isPending ? "Отправка..." : "Подтвердить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});
